import { createContext, useState } from 'react';

import { STEP_EVENTS } from '~/contexts/StepperContext/constants';
import type { SteppeContextProviderPropsType, StepperContextType, StepType } from '~/contexts/StepperContext/types';

export const StepperContext = createContext<StepperContextType>(null);

export default function StepperContextProvider({ children, steps }: SteppeContextProviderPropsType) {
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  const getCurrentStep = (): StepType => steps[currentStepIndex];

  const trigger = async (step: StepType, eventName: ValueOf<typeof STEP_EVENTS>): Promise<boolean> => {
    let result = null;
    if (typeof step?.[eventName] === 'function') {
      result = await step[eventName](context);
    }
    return result !== false;
  };

  const goToStepIndex = async (stepIndex: number) => {
    const newStep = steps[stepIndex];
    if (newStep) {
      if (
        (await trigger(getCurrentStep(), STEP_EVENTS.onLeaveStep)) &&
        (await trigger(newStep, STEP_EVENTS.onLoadStep))
      ) {
        setCurrentStepIndex(stepIndex);
      }
    }
  };

  const nextStep = async () => {
    const stepIndex = currentStepIndex + 1;
    if (await trigger(getCurrentStep(), STEP_EVENTS.onNextStep)) {
      await goToStepIndex(stepIndex);
    }
  };

  const previousStep = async () => {
    const stepIndex = currentStepIndex - 1;
    if (await trigger(getCurrentStep(), STEP_EVENTS.onPreviousStep)) {
      await goToStepIndex(stepIndex);
    }
  };

  const goToStep = async (newStep: string) => {
    const stepIndex = steps.findIndex(step => step.name === newStep);
    await goToStepIndex(stepIndex);
  };

  const context = {
    goToStepIndex,
    nextStep,
    previousStep,
    goToStep,
    currentStepIndex,
    currentStep: getCurrentStep(),
  };

  return <StepperContext.Provider value={context}>{children}</StepperContext.Provider>;
}
